import { type SyncStatus } from "./api.js";
import { formatAgo } from "./format.js";
import { useSync } from "./sync.js";

/**
 * How fresh the mirror is, without turning syncing into a job staff have to do.
 *
 * The server owns normal automatic syncing. Manual recovery actions live in
 * the account overflow menu, so the permanent sidebar only answers the useful
 * question: how fresh is the data I am looking at?
 */
export function SyncBadge() {
  const { status, refreshError } = useSync();

  if (!status) return null;

  return (
    <div className="sync sync-status-only">
      <div className="small muted sync-status-line">{describe(status)}</div>
      {status.last?.error && <div className="small sync-error">{status.last.error}</div>}
      {!!status.last?.skippedWaitlistCancellations && (
        <div className="small sync-error">
          {status.last.skippedWaitlistCancellations} waiting list cancellation{status.last.skippedWaitlistCancellations === 1 ? "" : "s"} skipped
        </div>
      )}
      {refreshError && <div className="small sync-error">{refreshError}</div>}
    </div>
  );
}

function describe(status: SyncStatus): string {
  if (status.running) return "Automatic sync running…";
  if (!status.last) return status.enabled ? "Automatic sync · waiting for first run" : "Never synced";

  const when = formatAgo(status.last.finishedAt ?? status.last.startedAt);
  if (status.last.error) return `Sync failed ${when}`;
  if (!status.enabled) return `Synced ${when} · automatic sync off`;
  return `Automatic · synced ${when}`;
}
